import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'https://helpdesk.uptimio.com/api';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Interceptor za dodavanje tokena
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Flag da spreči multiple concurrent refresh requests
let isRefreshing = false;
let failedQueue: any[] = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  
  failedQueue = [];
};

// Interceptor za error handling i auto-refresh
api.interceptors.response.use(
  (response) => {
    // Update activity timestamp na svaki uspešan request
    localStorage.setItem('lastActivity', Date.now().toString());
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    
    // Proveri da li je error 401 i da li je to na auth endpointu
    const isAuthEndpoint = originalRequest?.url?.includes('/auth/login') || 
                           originalRequest?.url?.includes('/auth/register') ||
                           originalRequest?.url?.includes('/auth/refresh-token') ||
                           originalRequest?.url?.includes('/invoices/public/');
    
    // Ako je 401 i NIJE auth endpoint, pokušaj refresh
    if (error.response?.status === 401 && !isAuthEndpoint && !originalRequest._retry) {
      if (isRefreshing) {
        // Ako se već refreshuje, dodaj request u queue
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(token => {
            originalRequest.headers['Authorization'] = 'Bearer ' + token;
            return api(originalRequest);
          })
          .catch(err => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = localStorage.getItem('refreshToken');
      
      if (!refreshToken) {
        // Nema refresh token, logout
        isRefreshing = false;
        localStorage.clear();
        window.location.href = '/login';
        return Promise.reject(error);
      }

      try {
        // Pokušaj da refreshuješ token
        const response = await axios.post(`${API_URL}/auth/refresh-token`, { refreshToken });
        const { token: newToken, refreshToken: newRefreshToken } = response.data;
        
        localStorage.setItem('token', newToken);
        localStorage.setItem('refreshToken', newRefreshToken);
        localStorage.setItem('lastActivity', Date.now().toString());
        
        // Update auth header
        api.defaults.headers.common['Authorization'] = 'Bearer ' + newToken;
        originalRequest.headers['Authorization'] = 'Bearer ' + newToken;
        
        processQueue(null, newToken);
        isRefreshing = false;
        
        return api(originalRequest);
      } catch (refreshError) {
        // Refresh failed, logout
        processQueue(refreshError, null);
        isRefreshing = false;
        localStorage.clear();
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }
    
    // Za sve ostale greške ili auth endpoint greške
    if (error.response?.status === 401 && isAuthEndpoint) {
      // Ne redirectuj, samo vrati error
      return Promise.reject(error);
    }
    
    return Promise.reject(error);
  }
);

export default api; 